import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

import { Button } from "@design-lab/shared/ui";
import { useNotification } from "@design-lab/shared/ui/notification";
import { safeRedirect } from "common/utils/safe-redirect";
import { cn } from "tailwind-variants";
import { useCanvaSettings } from "../api/useCanvaSettings";
import { CanvaModal } from "./CanvaModal";

const AUTO_START_QUERY_PARAM = "open_canva_modal_on_start";
const CANVA_ERROR_QUERY_PARAM = "canva_error";

type CanvaButtonProps = {
  autoOpen?: boolean;
  onAutoOpenComplete?: () => void;
  onFilesReady: (files: File[]) => Promise<void>;
  size?: "xl" | "sm" | "lg";
  allowMultipleSelection?: boolean;
  maxSelectionCount?: number;
};

export function CanvaButton({
  autoOpen = false,
  onAutoOpenComplete,
  onFilesReady,
  size,
  allowMultipleSelection = false,
  maxSelectionCount = 10
}: CanvaButtonProps) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { notify } = useNotification();

  const { data: settings, isLoading: isLoadingSettings } = useCanvaSettings(
    {
      source: "design-lab",
      redirect_url: location.pathname
    },
    isModalOpen || Boolean(searchParams.get(AUTO_START_QUERY_PARAM)) || autoOpen
  );

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  // Handle auto-open from prop
  useEffect(() => {
    if (autoOpen && settings?.authorized) {
      setIsModalOpen(true);
      onAutoOpenComplete?.();
    } else if (autoOpen && settings && !settings.authorized) {
      safeRedirect(settings.url, {
        additionalOrigins: ["https://www.canva.com", "https://canva.com"]
      });
    }
  }, [autoOpen, settings, onAutoOpenComplete]);

  useEffect(() => {
    if (Boolean(searchParams.get(AUTO_START_QUERY_PARAM)) && settings?.authorized) {
      searchParams.delete(AUTO_START_QUERY_PARAM);
      setSearchParams(searchParams);
      openModal();
    } else if (settings && !settings.authorized) {
      safeRedirect(settings.url, {
        additionalOrigins: ["https://www.canva.com", "https://canva.com"]
      });
    }
  }, [settings, searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get(CANVA_ERROR_QUERY_PARAM)) {
      searchParams.delete(CANVA_ERROR_QUERY_PARAM);
      setSearchParams(searchParams);
      notify({
        title: "Error",
        description: "Something went wrong, please try again.",
        variant: "error"
      });
    }
  }, [notify, searchParams, setSearchParams]);

  return (
    <>
      <Button
        data-testid="canva-button"
        size={size || "lg"}
        variant="outline"
        onPress={openModal}
        className={
          cn(size ? "px-2 lg:px-4" : "w-full max-w-40 border-black/20 px-4 py-2 lg:shrink")!
        }
      >
        <svg
          width="47"
          height="15"
          viewBox="0 0 47 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="min-h-4 min-w-12"
        >
          <path
            d="M46.6733 10.2526C46.5934 10.2526 46.5206 10.304 46.483 10.4116C46.0013 11.7797 45.3503 12.5935 44.8145 12.5935C44.5066 12.5935 44.3821 12.2521 44.3821 11.7166C44.3821 10.3765 45.1881 7.53508 45.5947 6.23948C45.6417 6.08279 45.6722 5.94247 45.6722 5.8232C45.6722 5.44668 45.4654 5.26193 44.9531 5.26193C44.4009 5.26193 43.8064 5.47708 43.2282 6.48503C43.0285 5.59635 42.4245 5.20814 41.5809 5.20814C40.6056 5.20814 39.6633 5.83255 38.8878 6.84518C38.1123 7.85781 37.2005 8.1899 36.5143 8.02619C37.0078 6.82413 37.1911 5.9261 37.1911 5.25959C37.1911 4.21422 36.6717 3.58279 35.8328 3.58279C34.5567 3.58279 33.8212 4.7942 33.8212 6.06876C33.8212 7.05332 34.2701 8.06595 35.257 8.55706C34.4298 10.4186 33.222 12.1024 32.7637 12.1024C32.1715 12.1024 31.9976 9.2189 32.0305 7.15622C32.0516 5.97287 32.1503 5.91207 32.1503 5.55426C32.1503 5.34846 32.0164 5.20814 31.4783 5.20814C30.2234 5.20814 29.8356 6.2652 29.7768 7.47895C29.7579 7.94021 29.6853 8.39772 29.5606 8.84238C29.0366 10.7039 27.9556 12.1165 27.2506 12.1165C26.9239 12.1165 26.8346 11.7914 26.8346 11.3658C26.8346 10.0257 27.589 8.35126 27.589 6.92236C27.589 5.87231 27.126 5.20814 26.2542 5.20814C25.2272 5.20814 23.8689 6.42423 22.5835 8.70206C23.0065 6.95744 23.1804 5.26894 21.9279 5.26894C21.6561 5.27298 21.3893 5.34196 21.15 5.47007C21.0696 5.50746 21.0025 5.56805 20.9572 5.64397C20.9119 5.71988 20.8907 5.80759 20.8962 5.8957C21.0161 7.75257 19.3922 12.5094 17.8529 12.5094C17.5733 12.5094 17.437 12.2077 17.437 11.7212C17.437 10.3789 18.2407 7.54443 18.6449 6.24649C18.6966 6.07811 18.7248 5.93312 18.7248 5.80683C18.7248 5.45136 18.5039 5.26894 18.0033 5.26894C17.4534 5.26894 16.8566 5.47708 16.2808 6.48503C16.0787 5.59635 15.4747 5.20814 14.6311 5.20814C13.2469 5.20814 11.7006 6.66511 11.0215 8.56408C10.1121 11.0992 8.27905 13.5477 5.81155 13.5477C3.572 13.5477 2.38995 11.6932 2.38995 8.76286C2.38995 4.53227 5.51075 1.07577 7.8255 1.07577C8.93235 1.07577 9.4611 1.77736 9.4611 2.85313C9.4611 4.15575 8.73025 4.76146 8.73025 5.25725C8.73025 5.40926 8.85715 5.55893 9.1086 5.55893C10.1144 5.55893 11.2988 4.3826 11.2988 2.7783C11.2988 1.17399 9.98985 0 7.6751 0C3.8493 0 0 3.83068 0 8.73948C0 12.645 1.9364 15 5.28515 15C7.56935 15 9.5645 13.232 10.6408 11.1693C10.763 12.8789 11.5432 13.7722 12.7323 13.7722C13.7898 13.7722 14.6452 13.1455 15.2985 12.044C15.5499 13.1969 16.2174 13.7605 17.0845 13.7605C18.0785 13.7605 18.9104 13.1338 19.7024 11.9691C19.6906 12.8835 19.8998 13.7442 20.6964 13.7442C21.0724 13.7442 21.5213 13.6576 21.6012 13.3302C22.4401 9.87839 24.5128 7.06034 25.1474 7.06034C25.3354 7.06034 25.3871 7.24041 25.3871 7.45323C25.3871 8.39102 24.722 10.3134 24.722 11.5412C24.722 12.8672 25.2883 13.7442 26.4586 13.7442C27.7558 13.7442 29.0742 12.1632 29.9531 9.85267C30.2281 12.0112 30.8226 13.7535 31.7532 13.7535C32.8953 13.7535 34.9233 11.3611 36.1524 8.82834C36.6341 8.88915 37.358 8.87278 38.0536 8.384C37.7575 9.13003 37.5835 9.94621 37.5835 10.7624C37.5835 13.1127 38.7115 13.7722 39.6821 13.7722C40.7372 13.7722 41.5927 13.1455 42.2483 12.044C42.4645 13.0379 43.0167 13.7582 44.0319 13.7582C45.6205 13.7582 47 12.1422 47 10.8162C47 10.4654 46.8496 10.2526 46.6733 10.2526ZM13.7005 12.4696C13.0589 12.4696 12.8075 11.8265 12.8075 10.8676C12.8075 9.20253 13.952 6.42189 15.1622 6.42189C15.6909 6.42189 15.8907 7.04163 15.8907 7.79935C15.8907 9.49018 14.8026 12.4696 13.7005 12.4696ZM35.6753 7.5C35.2923 7.04631 35.1536 6.42891 35.1536 5.87933C35.1536 5.20112 35.4027 4.62816 35.7012 4.62816C35.9996 4.62816 36.0913 4.92049 36.0913 5.32741C36.0913 6.00795 35.8469 7.00187 35.6753 7.5ZM40.6503 12.4696C40.0088 12.4696 39.7573 11.7306 39.7573 10.8676C39.7573 9.26099 40.9017 6.42189 42.1214 6.42189C42.6501 6.42189 42.8382 7.03695 42.8382 7.79935C42.8382 9.49018 41.7689 12.4696 40.6503 12.4696Z"
            fill="#0A0A0A"
          />
        </svg>
        {isLoadingSettings ? "..." : ""}
      </Button>
      {isModalOpen && !isLoadingSettings && settings?.authorized && (
        <CanvaModal
          closeModal={closeModal}
          onFilesReady={onFilesReady}
          allowMultipleSelection={allowMultipleSelection}
          maxSelectionCount={maxSelectionCount}
        />
      )}
    </>
  );
}
